import { alertRequiresNavAttention } from '@aloft/toolkit-shared';

import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  Typography,
  useModal,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Portal } from '@aloft/toolkit-sui/chakra';

import { ReactElement } from 'react';

import { useAppraisifyNavAlertBlock } from '../../../hooks/useAppraisifyNavAlertBlock.ts';
import { useProcessAlertTriggerForRoute } from '../../../hooks/useProcessAlertTriggerForRoute.ts';
import {
  VALIDATION_STEP_LABELS,
  fieldDisplayNames,
  routeToStepMap,
} from '../../../hooks/validation/consts.ts';
import {
  getTriggeredAlertsForRoute,
  useAppraisifyNavigation,
} from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { useAlerts } from '../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { useModuleContext } from '../../../modules/shared/moduleContext/useModuleContext.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectAlerts } from '../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectTriggeredAlerts } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { menuListProps } from '../consts.ts';
import { SubNavAlertBadge } from './SubNavAlertBadge.tsx';
import {
  APPRAISIFY_NAV_ALERT_BLOCK_TOOLTIP,
  APPRAISIFY_NAV_WARNING_BLOCK_TOOLTIP,
} from './constants.ts';
import { SubNavItemType, navItemHasChildren } from './types.ts';
import { useIsSelectedNavItem } from './useIsSelectedNavItem.ts';

const Contents = ({
  label,
  isSelected,
  isLegacyLayout,
  alertCount,
  alertSeverity,
}: {
  label: string;
  isSelected: boolean;
  isLegacyLayout: boolean;
  alertCount: number;
  alertSeverity: 'error' | 'warning';
}) => {
  const labelNode = (
    <Typography
      variant="body1"
      color={isSelected ? 'textPrimary' : 'textTertiary'}
      __dangerDoNotUseSx={{
        fontWeight: isSelected ? '600' : undefined,
        lineHeight: isLegacyLayout ? undefined : '21px',
      }}
    >
      {label}
    </Typography>
  );

  const hiddenLabelNode = (
    <Typography
      variant="body1"
      __dangerDoNotUseSx={{
        visibility: 'hidden',
        fontWeight: '600',
        height: 0,
        lineHeight: isLegacyLayout ? undefined : '21px',
      }}
    >
      {label}
    </Typography>
  );

  const tabLabel = (
    <Flex alignItems="center" gap="8px">
      {labelNode}
      {alertCount > 0 && <SubNavAlertBadge count={alertCount} severity={alertSeverity} />}
    </Flex>
  );

  return isLegacyLayout ? (
    <>
      {tabLabel}
      {hiddenLabelNode}
    </>
  ) : (
    <Flex flexDirection="column" justifyContent="center">
      {tabLabel}
      <Flex alignItems="center" gap="8px" __dangerDoNotUseSx={{ visibility: 'hidden', height: 0 }}>
        {labelNode}
        {alertCount > 0 && <SubNavAlertBadge count={alertCount} severity={alertSeverity} />}
      </Flex>
    </Flex>
  );
};

const SubNavItemWithAlertTooltip = ({
  children,
  showAlertTooltip,
  tooltipLabel,
}: {
  children: ReactElement;
  showAlertTooltip: boolean;
  tooltipLabel: string;
}) => {
  if (!showAlertTooltip) {
    return children;
  }

  return (
    <Tooltip
      label={tooltipLabel}
      placement="bottom"
      hasArrow
      openDelay={200}
      bg="textPrimary"
      color="textOffset"
    >
      {children}
    </Tooltip>
  );
};

export const HeaderSubNavItem = ({
  item,
  isLegacyLayout = false,
}: {
  item: SubNavItemType;
  isLegacyLayout?: boolean;
}) => {
  const { label, route, parentRoute } = item;
  const { isStepEnabled, getFirstErrorStep, countTriggeredAlertsForRoute } =
    useAppraisifyNavigation();
  const {
    navigation: { NavLink, useNavigate },
  } = useModuleContext();
  const triggeredAlertTypes = useAppSelector(selectTriggeredAlerts);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const { errors } = useAppraisifyValidation();
  const { Modal, openModal, isModalOpen, closeModal } = useModal();
  const navigate = useNavigate();

  const isSelected = useIsSelectedNavItem(route);

  const step = routeToStepMap[route];
  const isStepDisabled = step ? !isStepEnabled(step) : false;

  const alerts = useAlerts();
  const { blockForwardNavigationIfNeeded, blockCertifyIfNeeded, shouldShowNavAlertTooltip } =
    useAppraisifyNavAlertBlock();
  const processAlertTriggerForRoute = useProcessAlertTriggerForRoute();
  const alertCount = countTriggeredAlertsForRoute(alerts, route);
  const showAlertTooltip = shouldShowNavAlertTooltip(route, alertCount);
  const routeTriggeredAlertTypeSet = new Set(getTriggeredAlertsForRoute(triggeredAlertTypes, alerts, route));
  const routeTriggeredAlerts = Object.values(alerts)
    .flat()
    .filter(
      (alert) =>
        routeTriggeredAlertTypeSet.has(alert.type) &&
        alertRequiresNavAttention(alert, alertsCommentary, explanationContext),
    );
  const hasErrorAlerts = routeTriggeredAlerts.some((alert) => alert.severity === 'error');
  const alertSeverity = hasErrorAlerts ? 'error' : 'warning';
  const alertTooltipLabel =
    alertCount > 0 && !hasErrorAlerts
      ? APPRAISIFY_NAV_WARNING_BLOCK_TOOLTIP
      : APPRAISIFY_NAV_ALERT_BLOCK_TOOLTIP;

  const contents = (
    <Contents
      label={label}
      isSelected={isSelected}
      isLegacyLayout={isLegacyLayout}
      alertCount={alertCount}
      alertSeverity={alertSeverity}
    />
  );

  const contentsWithTooltip = (
    <SubNavItemWithAlertTooltip
      showAlertTooltip={showAlertTooltip}
      tooltipLabel={alertTooltipLabel}
    >
      {contents}
    </SubNavItemWithAlertTooltip>
  );

  const handleMenuButtonClick = (e: React.MouseEvent) => {
    const blocked = route === 'review' ? blockCertifyIfNeeded() : blockForwardNavigationIfNeeded(route);

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    processAlertTriggerForRoute(route);
    if (isStepDisabled) {
      openModal();
      return;
    }
    navigate(`${parentRoute}/${route}`);
  };

  const handleNavLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (route === 'review' ? blockCertifyIfNeeded() : blockForwardNavigationIfNeeded(route)) {
      return;
    }
    processAlertTriggerForRoute(route);
    if (isStepDisabled) {
      openModal();
      return;
    }
    navigate(`${parentRoute}/${route}`);
  };

  let Component: ReactElement;

  if (navItemHasChildren(item)) {
    Component = (
      <Menu>
        <MenuButton onClick={handleMenuButtonClick}>{contentsWithTooltip}</MenuButton>
        <Portal>
          <MenuList {...menuListProps}>
            {item.items.map((subItem) => {
              const subStep = routeToStepMap[subItem.route];
              const isSubStepDisabled = subStep ? !isStepEnabled(subStep) : false;

              return (
                <NavLink
                  key={subItem.route}
                  to={`${parentRoute}/${route}#${subItem.route}`}
                  onClick={(e: React.MouseEvent) => {
                    if (isSubStepDisabled) {
                      e.preventDefault();
                      e.stopPropagation();
                      openModal();
                    }
                  }}
                >
                  <MenuItem>
                    <Typography variant="body1" __testId={subItem.testID}>
                      {subItem.label}
                    </Typography>
                  </MenuItem>
                </NavLink>
              );
            })}
          </MenuList>
        </Portal>
      </Menu>
    );
  } else {
    Component = (
      <NavLink to={`${parentRoute}/${route}`} onClick={handleNavLinkClick}>
        {contentsWithTooltip}
      </NavLink>
    );
  }

  const firstErrorStep = getFirstErrorStep();
  const shouldShowValidationModal =
    isModalOpen &&
    firstErrorStep &&
    firstErrorStep !== 'OrderDetails' &&
    firstErrorStep !== 'Analysis' &&
    firstErrorStep !== 'Commentary' &&
    firstErrorStep !== 'Reconciliation';

  const formatValidationFieldKeyForDisplay = (key: string) =>
    key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const getRequiredFields = () => {
    if (!firstErrorStep) return [];

    const stepErrors = errors[firstErrorStep];
    if (!stepErrors) return [];

    return Object.keys(stepErrors).map((fieldKey) => {
      const error = stepErrors[fieldKey as keyof typeof stepErrors];
      return {
        key: fieldKey,
        displayName: fieldDisplayNames[fieldKey] ?? formatValidationFieldKeyForDisplay(fieldKey),
        message: error?.message ?? 'Required',
      };
    });
  };

  const requiredFields = getRequiredFields();

  return (
    <>
      <Flex
        paddingLeft="0.5rem"
        paddingRight="0.5rem"
        height="100%"
        alignItems="center"
        id={`${route}-nav`}
      >
        {Component}
      </Flex>

      {shouldShowValidationModal && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title="Incomplete Required Fields"
          secondaryAction={{
            text: 'OK',
            onClick: closeModal,
          }}
        >
          <div style={{ marginBottom: '1rem' }}>
            <Typography variant="body1">
              Please complete all required fields in the&nbsp;
              <strong>
                {firstErrorStep
                  ? (VALIDATION_STEP_LABELS[firstErrorStep] ?? 'previous step')
                  : 'previous step'}
              </strong>
              &nbsp; before continuing to <strong>{label}</strong>.
            </Typography>
          </div>

          {requiredFields.length > 0 && (
            <div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Typography variant="body2" __dangerDoNotUseSx={{ fontWeight: '600' }}>
                  Required fields:
                </Typography>
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {requiredFields.map((field) => (
                  <li key={field.key}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <Typography variant="body2">
                        <strong>{field.displayName}</strong>
                      </Typography>
                      {field.message !== 'Required' && (
                        <Typography variant="body2" color="textTertiary">
                          ({field.message})
                        </Typography>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
